import { FunctionComponent } from 'preact';
import { useState, useEffect, Dispatch, useMemo } from 'preact/hooks';
import type { SetStateAction } from 'preact/compat';
import clsx from 'clsx';
import { RenderModeType } from '@accedo/ctv-ui-shared';
import { nav } from '##/services/navigation';
import styles from './LoadingIndicator.module.scss';

type LoadingIndicatorProps = {
  showOnMount?: boolean;
  solidBackground?: boolean;
  renderMode?: RenderModeType;
};

let loadingIndicatorVisibility: Dispatch<SetStateAction<boolean>> | null;
let loadingSolidBackground: Dispatch<SetStateAction<boolean>> | null;
let loadingRenderMode: Dispatch<SetStateAction<RenderModeType>> | null;

export const setLoadingIndicatorVisibility = (visibility: boolean, doNotBlockNavigation?: boolean): void => {
  loadingIndicatorVisibility?.(visibility);
  if (!doNotBlockNavigation !== false) {
    nav.setIgnoreKeyEvents(visibility, !visibility);
  }
};

export const setLoadingSolidBackground = (visibility: boolean): void => {
  loadingSolidBackground?.(visibility);
};

export const setLoadingRenderMode = (value: RenderModeType): void => {
  loadingRenderMode?.(value);
};

export const LoadingIndicator: FunctionComponent<LoadingIndicatorProps> = ({
  showOnMount = true,
  solidBackground = false,
  renderMode = 'low',
}) => {
  const [show, setShow] = useState(showOnMount);
  const [isSolidBackground, setIsSolidBackground] = useState(solidBackground);
  const [render, setLoadingRenderMode] = useState(renderMode);

  useEffect(() => {
    loadingIndicatorVisibility = setShow;
    loadingSolidBackground = setIsSolidBackground;
    loadingRenderMode = setLoadingRenderMode;

    return () => {
      loadingIndicatorVisibility = null;
      loadingSolidBackground = null;
      loadingRenderMode = null;
    };
  }, []);

  /* TEsting different types of loading indicators */
  // Not sure if we're deprecating this
  // const LoadingBar = useMemo(
  //   () => (
  //     <div className={styles.loadingBar}>
  //       <div />
  //       <div />
  //       <div />
  //       <div />
  //       <div />
  //     </div>
  //   ),
  //   [],
  // );

  const LoadingSpinner = useMemo(() => <span className={styles.loadingRing} />, []);

  const LoadingDots = useMemo(
    () => (
      <div className={styles.loadingDots}>
        <div className={styles.dot} />
        <div className={styles.dot} />
        <div className={styles.dot} />
        <div className={styles.dot} />
        <div className={styles.dot} />
        <div className={styles.dot} />
        <div className={styles.dot} />
        <div className={styles.dot} />
      </div>
    ),
    [],
  );

  return show ? (
    <div className={clsx(styles.container, isSolidBackground ? styles.isSolid : styles.isOverlay)}>
      {render === 'high' ? LoadingSpinner : LoadingDots}
    </div>
  ) : null;
};
